import type {
  MeasurementPoint,
  PlanMark,
  PlatformEvent,
  SiteNode,
} from "@energyplatform/client";
import { pathTo } from "./tree.ts";
import type { Sample } from "./analysis.ts";

// Pure logic of floor plans and the site map, shared by the page and its tests.

export interface Geo {
  latitude: number;
  longitude: number;
  /** The node the position is taken from: the node itself, or the nearest ancestor that has one. */
  from: string;
}

/** Where a node is: its own position, else the nearest ancestor's (a building inherits the site's address). */
export function geoOf(nodes: readonly SiteNode[], id: string): Geo | null {
  for (const n of pathTo(nodes, id).reverse()) {
    if (n.latitude !== null && n.longitude !== null) {
      return { latitude: n.latitude, longitude: n.longitude, from: n.id };
    }
  }
  return null;
}

const NICE = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];

/**
 * A scale bar of a round length about a sixth of the plan's width: its length in metres and as a fraction of the
 * image width.
 */
export function scaleBar(widthM: number): { metres: number; fraction: number } {
  const metres = [...NICE].reverse().find((m) => m <= widthM / 6) ?? NICE[0]!;
  return { metres, fraction: metres / widthM };
}

/** Area the plan shows in m², from its real width and the image's proportions. */
export function planArea(widthM: number, w: number, h: number): number {
  return widthM * ((widthM * h) / w);
}

/** Rotation of the north arrow in degrees, clockwise from "up"; null when the plan has no orientation. */
export function northAngle(orientation: string): number | null {
  switch (orientation) {
    case "north-up":
      return 0;
    case "north-right":
      return 90;
    case "north-down":
      return 180;
    case "north-left":
      return 270;
    default:
      return null;
  }
}

export const HISTORY_HOURS = 7 * 24;

/** Start of the hour `back` hours before the current hour (0 = the current hour). */
export function hourStart(now: number, back: number): number {
  const h = new Date(now);
  h.setMinutes(0, 0, 0);
  return h.getTime() - back * 3_600_000;
}

/** Mean of the samples in [start, start + 1 h); null when none has a value. */
export function hourMean(
  samples: readonly Sample[],
  start: number,
): number | null {
  let sum = 0;
  let n = 0;
  for (const s of samples) {
    if (s.t >= start && s.t < start + 3_600_000 && s.v !== null) {
      sum += s.v;
      n++;
    }
  }
  return n === 0 ? null : sum / n;
}

/** Points with an event of theirs that was on at `at` (at = now: the open ones). */
export function alarmedAt(
  events: readonly PlatformEvent[],
  at: number,
): Set<string> {
  return new Set(
    events
      .filter(
        (e) =>
          e.subjectType === "point" &&
          new Date(e.startedAt).getTime() <= at &&
          (e.endedAt === null || new Date(e.endedAt).getTime() > at),
      )
      .map((e) => e.subjectId),
  );
}

/**
 * Points placed together: the points one device measures (e.g. three phases and their sum) are one thing on a plan,
 * placed with one click and shown as one marker with several values. A point without a device is a unit of its own.
 */
export interface PlanUnit {
  key: string;
  pointIds: string[];
  /** What the points have in common ("Zamel PV 50kW"), or the point's name. */
  title: string;
  /** Per point, what is left of its name after the title ("Faza L1"); empty for the device's own point. */
  names: string[];
}

const SEPARATOR = " · ";

/** The leading name parts all names share, and what is left of each. */
export function commonTitle(names: readonly string[]): {
  title: string;
  rest: string[];
} {
  if (names.length === 1) {
    return { title: names[0]!, rest: [""] };
  }
  const parts = names.map((n) => n.split(SEPARATOR));
  let shared = 0;
  while (
    parts.every((p) => p.length > shared) &&
    parts.every((p) => p[shared] === parts[0]![shared])
  ) {
    shared++;
  }
  // Nothing shared by name: the names stay whole and the first one titles the marker.
  return shared === 0
    ? { title: names[0]!, rest: [...names] }
    : {
        title: parts[0]!.slice(0, shared).join(SEPARATOR),
        rest: parts.map((p) => p.slice(shared).join(SEPARATOR)),
      };
}

function unitOf(key: string, points: MeasurementPoint[]): PlanUnit {
  const sorted = [...points].sort(
    (a, b) => a.name.length - b.name.length || a.name.localeCompare(b.name),
  );
  const { title, rest } = commonTitle(sorted.map((p) => p.name));
  return { key, pointIds: sorted.map((p) => p.id), title, names: rest };
}

/** The points of a place as units to place: one per device, one per point without a device. */
export function planUnits(points: readonly MeasurementPoint[]): PlanUnit[] {
  const byDevice = new Map<string, MeasurementPoint[]>();
  const units: PlanUnit[] = [];
  for (const p of points) {
    if (p.binding) {
      const list = byDevice.get(p.binding.deviceId) ?? [];
      list.push(p);
      byDevice.set(p.binding.deviceId, list);
    } else {
      units.push(unitOf(p.id, [p]));
    }
  }
  for (const [device, list] of byDevice) {
    units.push(unitOf(`device:${device}`, list));
  }
  return units.sort((a, b) => a.title.localeCompare(b.title));
}

/** A marker on a plan: the marks of one device at one spot, or a single mark. */
export interface MarkGroup extends PlanUnit {
  x: number;
  y: number;
}

/**
 * Marks as markers: marks of the same device at the same spot are one marker (placed together they always are);
 * a device's points placed apart stay apart.
 */
export function groupMarks(
  marks: readonly PlanMark[],
  points: readonly MeasurementPoint[],
): MarkGroup[] {
  const byId = new Map(points.map((p) => [p.id, p]));
  const groups = new Map<
    string,
    { x: number; y: number; points: MeasurementPoint[] }
  >();
  for (const m of marks) {
    const p = byId.get(m.pointId);
    if (!p) {
      continue;
    }
    const key = p.binding ? `device:${p.binding.deviceId}@${m.x},${m.y}` : p.id;
    const g = groups.get(key) ?? { x: m.x, y: m.y, points: [] };
    g.points.push(p);
    groups.set(key, g);
  }
  return [...groups].map(([key, g]) => ({
    ...unitOf(key, g.points),
    x: g.x,
    y: g.y,
  }));
}

/** A label to place: its marker's position and the label's size, in one unit (screen px or plan units). */
export interface LabelBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Rect {
  l: number;
  t: number;
  r: number;
  b: number;
}

const overlap = (a: Rect, b: Rect) =>
  Math.max(0, Math.min(a.r, b.r) - Math.max(a.l, b.l)) *
  Math.max(0, Math.min(a.b, b.b) - Math.max(a.t, b.t));

/**
 * Where each label goes so that labels do not cover each other or other markers: to the right of its marker if
 * there is room, else left, below, above or diagonally; when nothing is free, where it covers least. Returns the
 * label's top-left corner relative to its marker. Greedy, in the given order, so the result is stable.
 */
export function placeLabels(
  boxes: readonly LabelBox[],
  dot: number,
  gap: number,
): { dx: number; dy: number }[] {
  const dots: Rect[] = boxes.map((b) => ({
    l: b.x - dot,
    t: b.y - dot,
    r: b.x + dot,
    b: b.y + dot,
  }));
  const placed: Rect[] = [];
  return boxes.map((b, i) => {
    const d = dot + gap;
    const candidates = [
      { dx: d, dy: -b.h / 2 },
      { dx: -d - b.w, dy: -b.h / 2 },
      { dx: -b.w / 2, dy: d },
      { dx: -b.w / 2, dy: -d - b.h },
      { dx: d, dy: gap },
      { dx: d, dy: -b.h - gap },
      { dx: -d - b.w, dy: gap },
      { dx: -d - b.w, dy: -b.h - gap },
    ];
    let best = candidates[0]!;
    let least = Infinity;
    for (const c of candidates) {
      const r = {
        l: b.x + c.dx,
        t: b.y + c.dy,
        r: b.x + c.dx + b.w,
        b: b.y + c.dy + b.h,
      };
      const covered =
        placed.reduce((s, p) => s + overlap(r, p), 0) +
        dots.reduce((s, p, j) => (j === i ? s : s + overlap(r, p)), 0);
      if (covered < least) {
        least = covered;
        best = c;
        if (covered === 0) {
          break;
        }
      }
    }
    placed.push({
      l: b.x + best.dx,
      t: b.y + best.dy,
      r: b.x + best.dx + b.w,
      b: b.y + best.dy + b.h,
    });
    return best;
  });
}
